import { ApiResult, HttpClient } from "../http.js";
import type { CompanyReportDto, ReportPeriod, WeeklyInsightsDto } from "../types/company-economy.js";

/**
 * Company economy for Indsigter and Rapporter. Every number is computed server-side on the same
 * calculator as the project's Økonomi tab.
 *
 * Permissions: the week needs `insights.view`; the reports and their CSV need `reports.view`. The
 * bonus section needs `bonus.view` (everyone's rows: `bonus.viewAll`); case rows follow
 * `budgettering.viewAllBudgets`.
 */
export class CompanyEconomyApi {
  constructor(private readonly http: HttpClient) {}

  /** GET /api/economy/insights/week?week=YYYY-Www (default: the current ISO week). */
  getWeekInsights(week?: string): Promise<ApiResult<WeeklyInsightsDto>> {
    return this.http.get<WeeklyInsightsDto>("/api/economy/insights/week", {
      query: week ? { week } : {},
    });
  }

  /**
   * GET /api/economy/reports?period=month|quarter|year&anchor=yyyy-MM-dd (default: month, today).
   * `year` is year-to-date at the anchor.
   */
  getCompanyReport(period: ReportPeriod = "month", anchor?: string): Promise<ApiResult<CompanyReportDto>> {
    return this.http.get<CompanyReportDto>("/api/economy/reports", {
      query: anchor ? { period, anchor } : { period },
    });
  }

  /**
   * GET /api/economy/reports/export.csv: the same report as a semicolon-separated CSV (Danish decimal
   * comma, UTF-8 with BOM). Returned as bytes through the authenticated client; a bare link would not
   * carry the bearer token. Wrap the buffer in a `Blob` to download it.
   */
  exportReportCsv(period: ReportPeriod = "month", anchor?: string): Promise<ApiResult<ArrayBuffer>> {
    return this.http.getBinary("/api/economy/reports/export.csv", {
      query: anchor ? { period, anchor } : { period },
      headers: { accept: "text/csv" },
    });
  }
}
